import { Subscription } from 'rxjs';
import { fabric } from 'fabric';
import { Injector } from '@angular/core';
import { PdfViewerV2Component } from '../../pdf-canvas-item/pdf-viewer-v2/pdf-viewer-v2.component';
import { ItemModel } from '../../../../models/itemModel';
import { FrameItem } from '../../../../models/session';
import { ResourceItemModel } from '../../../../models/resource';
import {
  PDF_PAGE_DEFAULT_HEIGHT,
  PDF_PAGE_DEFAULT_WIDTH,
} from '../../iframe/additional-apps.utils';
import { PdfPageToFabricService } from '../../pdf-canvas-item/pdf-services/pdf-page-to-fabric.service';
import { PdfManagerService } from '../../pdf-canvas-item/pdf-services/pdf-manager.service';
import { Position } from '../../../../services/items-canvas.service';
import { TelemetryService } from '../../../../services/telemetry.service';
import { FLAGS } from '../../../../services/flags.service';
import { PdfViewerComponent } from '../../pdf-canvas-item/pdf-viewer/pdf-viewer.component';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { BoardItemComponentClass, FabricControls } from './board.item';
import { CreationContext } from './board-item-configurations';
import { PDFConfigurations } from './board-pdf-configurations';

export type FabricPdfPage = fabric.Image & {
  // needed to set the top when moving the parent PDF
  shiftFromTop?: number;
  skipRemoteAddition?: boolean;
  skipRemoteDeletion?: boolean;
  pageIndex?: number;
};

export class FabricGroup extends fabric.Group {
  constructor({ top, left, width, height }) {
    super(
      [
        // This is a placeholder because we can not set the following property directly on the group and have the expected effect.
        new fabric.Rect({
          top,
          left,
          width,
          height,
          opacity: 0.01,
        }),
      ],
      { perPixelTargetFind: false },
    );
  }

  static _fromObject(
    className: string,
    object: Object,
    callback?: Function,
    extraParam?: any,
  ): fabric.Group {
    return new fabric.Group();
  }
}
export const PDF_RENDERING_ERROR_EVENT_NAME = 'PDF Rendering Error';

const PDFInvisibleControls = Object.values(FabricControls);
export class BoardPdf extends BoardItemComponentClass<
  PDFConfigurations,
  PdfViewerV2Component | PdfViewerComponent
>(FabricGroup) {
  AngularComponentClass = this.flagsService.isFlagEnabled(FLAGS.RENDER_PDFS_IN_FABRIC)
    ? PdfViewerV2Component
    : PdfViewerComponent;

  private contentSizeSubscription?: Subscription;
  private viewportEntrySubscription?: Subscription;

  private pdfPageToFabricService: PdfPageToFabricService;
  private pdfManagerService: PdfManagerService;
  private telemetryService: TelemetryService;
  private boardItemsInteractionsService: BoardItemsInteractionsManager;

  private pagesToRenderSubscription?: Subscription;

  private sizeSetBasedOnContent = false;

  constructor(
    injector: Injector,
    configurations: PDFConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.PDF, configurations, PDFInvisibleControls, creationContext);

    // If there is no url this means that the PDF is still uploading
    // So the loading indicator exists, and it shouldn't be selectable.
    this.selectable = !!configurations.url;
    this.focusOnInsertion =
      creationContext.isNewlyCreatedItem && creationContext.renderInNearestFreeSpace;

    this.pdfPageToFabricService = injector.get<PdfPageToFabricService>(PdfPageToFabricService);
    this.pdfManagerService = injector.get<PdfManagerService>(PdfManagerService);
    this.telemetryService = injector.get<TelemetryService>(TelemetryService);
    this.boardItemsInteractionsService = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );
  }

  protected async onFabricAdded() {
    super.onFabricAdded();

    if (this.isV2Pdf()) {
      this.subscribeToViewportEntry();
      this.subscribeToPageRendering();
    } else {
      this.subscribeToContentSize();
    }
  }

  private subscribeToContentSize() {
    this.contentSizeSubscription?.unsubscribe();
    this.contentSizeSubscription = this.componentRef?.instance.contentSizeAcquired.subscribe(
      this.updateRelatedFabricObjectSize.bind(this),
    );
  }

  private subscribeToViewportEntry() {
    this.viewportEntrySubscription?.unsubscribe();
    this.viewportEntrySubscription = this.pdfPageToFabricService
      .getPdfEnteredViewportSubject$(this.uid)
      .subscribe((isInViewport) => {
        if (isInViewport) {
          this.setSizeBasedOnPdfContent();
          // Unsubscribe since we only need to set the size once
          this.viewportEntrySubscription?.unsubscribe();
          this.viewportEntrySubscription = undefined;
        }
      });
  }

  subscribeToPageRendering() {
    if (this.isOnStaticCanvas()) {
      return;
    }

    this.pagesToRenderSubscription?.unsubscribe();
    this.pagesToRenderSubscription = this.pdfPageToFabricService
      .getPdfPageShouldRenderSubject$(this.uid)
      .subscribe(async (pageIndex) => {
        try {
          const fabricPdfPage = await this.pdfPageToFabricService.getPdfPageAsFabricImage(
            this.configurations.contentId,
            this.configurations.url!, // We know that url is defined since we got a value from page should render observable
            pageIndex,
          );

          fabricPdfPage.pageIndex = pageIndex;

          this.addPageToGroup(fabricPdfPage);

          this.pdfPageToFabricService.emitPdfPageRenderCompleted(this.uid, pageIndex);
        } catch (e) {
          this.telemetryService.errorEvent(PDF_RENDERING_ERROR_EVENT_NAME, {
            error: e,
            pdfConfiguration: this.configurations,
            level: 'converting to fabric image',
          });
        }
      });
  }

  protected onFabricRemoved() {
    super.onFabricRemoved();

    this.contentSizeSubscription?.unsubscribe();
    this.pagesToRenderSubscription?.unsubscribe();
    this.viewportEntrySubscription?.unsubscribe();
  }

  protected setStateFromConfigurations(configurations: PDFConfigurations) {
    // Overriding width and height update because it should only depend on the size of the content
    configurations.height = this.height ?? PDF_PAGE_DEFAULT_HEIGHT;
    configurations.width = this.width ?? PDF_PAGE_DEFAULT_WIDTH;
    super.setStateFromConfigurations(configurations);
  }

  async updateRelatedFabricObjectSize(size?: { width: number; height: number }) {
    if (!size) {
      return;
    }
    if (!this.isOnStaticCanvas()) {
      // We don't need to do this for static canvas in order to keep the preview as concise as possible
      //  and preserve a good view of all object (to not make the preview too big and hence items don't show)
      (this as fabric.Object).set({
        width: size.width,
        height: size.height,
        selectable: true,
      });
      this.setCoords();
      this.canvas?.requestRenderAll();
    }
  }

  async setSizeBasedOnPdfContent() {
    if (this.sizeSetBasedOnContent) {
      return;
    }
    if (this.isOnStaticCanvas()) {
      return;
    }
    if (!this.configurations.url) {
      return;
    }
    const { width, height } = await this.pdfManagerService.getPdfSize(
      this.configurations.contentId,
      this.configurations.url,
    );

    // We don't need to do this for static canvas in order to keep the preview as concise as possible
    //  and preserve a good view of all object (to not make the preview too big and hence items don't show)
    (this as fabric.Object).set({
      selectable: true,
    });

    (this as any as fabric.Group)._objects[0].set({
      width,
      height,
    });

    // So the size of the group updates after we updated the size of the placeholder element
    (this as any as fabric.Group).addWithUpdate();
    this.setCoords();
    this.canvas?.requestRenderAll();

    this.sizeSetBasedOnContent = true;
  }

  override openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = true;
    this.lockScalingY = true;
    this.lockUniScaling = false;
    this.lockSkewingX = false;
    this.lockSkewingY = false;
    this.lockScalingFlip = false;

    this.evented = true;
    this.propagateCanvasMouseDownToObject = false; // When the object is locked we don't need this as the behavior will be normal and we don't want an extra performance overhead.
  }

  closeLock() {
    super.closeLock();

    // when the pdf is locked we need to treat it as if it doesn't exist, to enable multi-select of scribbles on top of it
    // We instead, can implement the needed actions on mouse down in the canvasMouseDown method.
    this.evented = false;
    this.propagateCanvasMouseDownToObject = true;
  }

  protected onCanvasMouseDown(mouseDownAffectedOtherObjects: boolean) {
    super.onCanvasMouseDown(mouseDownAffectedOtherObjects);
    if (mouseDownAffectedOtherObjects) {
      // the click was on another object above the PDF and the object didn't receive another click
      return;
    }

    this.showLockSnackBarIfObjectLocked();

    this.canvas?.setActiveObject?.(this);
    this.canvas?.requestRenderAll(); // to render the blue border
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();

    // No need to sync the height as we don't want the loading indicator and the preview to take the height of all pages
    encodedBoardItem.position?.set('height', `${PDF_PAGE_DEFAULT_HEIGHT}`);

    return {
      ...encodedBoardItem,
      model: ItemModel.Resource,
      resource_state: {
        resource_url: this.configurations.url,
        resource_item_model: ResourceItemModel.PDF,
      },
      content_id: this.configurations.contentId ?? '',
      migratedFromHtmlToFabric: this.configurations.migratedFromHtmlToFabric,
    };
  }

  override hasHtmlContent() {
    // Even though it does have HTML,
    // we lie because it is not the HTML of the content it is only the overlay/loading indicator which should not affect the user experience with the stacking order
    return !this.isV2Pdf();
  }

  private getPdfPagePosition(position: Position, pdfPage: FabricPdfPage): Position {
    // previously, the PDF pages was rendered at a 0.95 scale, so there is a total padding of 0.05 horizontally and the same vertically
    // this means to have a PDF at the exact same place at before,
    // we need to shift each page to the bottom right by (1 - 0.95 = 0.05) / 2  of the original size
    const constantPadding = 1.2; // This is used to make the PDF with the new rendering method align perfectly like the old one,
    // this value was determined by testing and comparison with old PDFs
    const leftPadding = ((pdfPage.width ?? 0) * 0.05 * (pdfPage.scaleX ?? 0)) / 2 + constantPadding;
    const topPadding = ((pdfPage.height ?? 0) * 0.05 * (pdfPage.scaleY ?? 0)) / 2 + constantPadding;
    return {
      left: position.left + leftPadding,
      top: position.top + (pdfPage.shiftFromTop ?? 0) + topPadding,
    };
  }

  private isV2Pdf() {
    return this.flagsService.isFlagEnabled(FLAGS.RENDER_PDFS_IN_FABRIC);
  }

  private addPageToGroup(fabricPdfPage: FabricPdfPage) {
    if (!this.group) {
      const { top, left } = this.getPdfPagePosition(
        { left: this.left ?? 0, top: this.top ?? 0 },
        fabricPdfPage,
      );

      fabricPdfPage.top = top;
      fabricPdfPage.left = left;
      (this as any as fabric.Group).addWithUpdate(fabricPdfPage);
    } else {
      // We ungroup the current active selection, add the new page and then group again
      // This is so that we don't break the selection and the position of the pdf page is calculated correctly
      const activeObjects = [...(this.canvas?.getActiveObjects() ?? [])];

      this.boardItemsInteractionsService.triggerDeselectAll();

      const { top, left } = this.getPdfPagePosition(
        { left: this.left ?? 0, top: this.top ?? 0 },
        fabricPdfPage,
      );

      fabricPdfPage.top = top;
      fabricPdfPage.left = left;

      (this as any as fabric.Group).addWithUpdate(fabricPdfPage);

      this.canvas?.setActiveObject(
        new fabric.ActiveSelection(activeObjects, { canvas: this.canvas }),
      );
    }
    this.canvas?.requestRenderAll();
  }
}
